import { sliceChars, findIndex1, replaceAllLiteral, parseAmountToCents } from "@capture/shared";
import { TXN_COLLECTIONS } from "../transaction-context.js";
import { checkDigitValid } from "./check-digit.js";
const MAX_STEPS = 10_000;
/**
 * Evaluate an AST against a context. Returns a scalar value.
 */
export function evaluate(ast, context) {
    let steps = 0;
    function eval_(node, ctx) {
        steps++;
        if (steps > MAX_STEPS) {
            throw new Error("Expression evaluation exceeded maximum step count");
        }
        switch (node.type) {
            case "literal":
                return node.value;
            case "fieldRef":
                return resolveFieldRef(node.name, ctx);
            case "binaryOp":
                return evalBinaryOp(node.op, eval_(node.left, ctx), eval_(node.right, ctx));
            case "unaryOp":
                return evalUnaryOp(node.op, eval_(node.operand, ctx));
            case "functionCall":
                return evalFunction(node.name, node.args, eval_, ctx);
        }
    }
    return eval_(ast, context);
}
// --- Field resolution ---
function resolveFieldRef(name, context) {
    // Simple field
    if (!name.includes(".")) {
        const val = context.fields[name];
        if (val === undefined || val === null)
            return null;
        return val;
    }
    // F-025 — `batch.<prop>` / `document.<prop>` resolve a system property
    // (e.g. {batch.scannedAt}, {batch.documentCount}) so batch-scope policy
    // formulas can reference system metadata + temporal facts without those
    // keys colliding with header field names.
    const dot = name.indexOf(".");
    const head = name.slice(0, dot);
    const prop = name.slice(dot + 1);
    // Per-row overlay (set while evaluating a TXN_* or *_LINES filter) takes
    // priority for dotted refs whose head matches the active collection/group.
    const rowOverlay = context._rowOverlay;
    if (rowOverlay && head === rowOverlay.head) {
        const v = rowOverlay.row[prop];
        return v === undefined || v === null ? null : v;
    }
    // SP5 — `transaction.<prop>` resolves transaction scalars + promoted fields.
    if (head === "transaction" && context.transaction && prop in context.transaction) {
        const v = context.transaction[prop];
        return v === undefined || v === null ? null : v;
    }
    if (head === "batch" && context.batch && prop in context.batch) {
        const v = context.batch[prop];
        return v === undefined || v === null ? null : v;
    }
    if (head === "document" && context.document && prop in context.document) {
        const v = context.document[prop];
        return v === undefined || v === null ? null : v;
    }
    // P8 — `const.<name>` resolves a job-level named constant. Date constants are
    // ISO strings and compare lexically like every other date value.
    if (head === "const" && context.constants) {
        const v = context.constants[prop];
        return v === undefined || v === null ? null : v;
    }
    // P8 — `user.<prop>` in expressions: scalar props only (id / teamId). `roles`
    // is an array and has no scalar expression form → null (documented; use a
    // `user.roles contains …` condition instead).
    if (head === "user" && context.user) {
        if (prop === "id")
            return context.user.id ?? null;
        if (prop === "teamId")
            return context.user.teamId ?? null;
        return null;
    }
    // Dotted: used inside aggregation functions. Return the raw ref name
    // so aggregation functions can resolve it themselves.
    // If someone writes {lineItems.amount} outside an aggregation function,
    // we can't resolve it — return null.
    return null;
}
// --- Binary operators ---
function evalBinaryOp(op, left, right) {
    // Null propagation for arithmetic (not for comparison/logical)
    if (op === "+" || op === "-" || op === "*" || op === "/" || op === "%") {
        if (left === null || right === null)
            return null;
        const l = toNum(left);
        const r = toNum(right);
        if (isNaN(l) || isNaN(r))
            return null;
        switch (op) {
            case "+": return l + r;
            case "-": return l - r;
            case "*": return l * r;
            case "/": return r === 0 ? null : l / r;
            case "%": return r === 0 ? null : l % r;
        }
    }
    // Comparison
    if (op === "==" || op === "!=") {
        const eq = looseEquals(left, right);
        return op === "==" ? eq : !eq;
    }
    if (op === ">" || op === "<" || op === ">=" || op === "<=") {
        if (left === null || right === null)
            return false;
        const l = toNum(left);
        const r = toNum(right);
        if (!isNaN(l) && !isNaN(r)) {
            switch (op) {
                case ">": return l > r;
                case "<": return l < r;
                case ">=": return l >= r;
                case "<=": return l <= r;
            }
        }
        // Fallback to string comparison (dates, etc.)
        const ls = String(left);
        const rs = String(right);
        switch (op) {
            case ">": return ls > rs;
            case "<": return ls < rs;
            case ">=": return ls >= rs;
            case "<=": return ls <= rs;
        }
    }
    // Logical
    if (op === "&&")
        return toBool(left) && toBool(right);
    if (op === "||")
        return toBool(left) || toBool(right);
    return null;
}
// --- Unary operators ---
function evalUnaryOp(op, operand) {
    if (op === "!")
        return !toBool(operand);
    if (op === "-") {
        if (operand === null)
            return null;
        const n = toNum(operand);
        return isNaN(n) ? null : -n;
    }
    return null;
}
// --- Functions ---
function evalFunction(name, argNodes, eval_, context) {
    // Line item aggregation functions handle args specially
    if (name === "SUM_LINES" ||
        name === "COUNT_LINES" ||
        name === "COUNT_DISTINCT_LINES" ||
        name === "AVG_LINES" ||
        name === "MIN_LINES" ||
        name === "MAX_LINES") {
        return evalLineItemAgg(name, argNodes, context, eval_);
    }
    // Batch aggregation functions handle args specially (field refs resolved against batch documents)
    if (name === "BATCH_COUNT" || name === "BATCH_SUM" || name === "BATCH_AVG" || name === "BATCH_MIN" || name === "BATCH_MAX" || name === "BATCH_FIRST" || name === "BATCH_LAST" || name === "BATCH_ALL_EQUAL") {
        return evalBatchAgg(name, argNodes, context);
    }
    // SP5 — transaction-scoped aggregation functions (TXN_SUM/COUNT/MIN/MAX)
    if (name === "TXN_SUM" || name === "TXN_COUNT" || name === "TXN_MIN" || name === "TXN_MAX") {
        return evalTxnAgg(name, argNodes, context, eval_);
    }
    // Evaluate args normally
    const args = argNodes.map((n) => eval_(n, context));
    switch (name) {
        // Math
        case "SUM":
            return args.reduce((sum, v) => sum + (toNum(v) || 0), 0);
        case "ROUND": {
            const val = toNum(args[0] ?? null);
            const places = toNum(args[1] ?? 0);
            if (isNaN(val))
                return null;
            const factor = Math.pow(10, places);
            return Math.round(val * factor) / factor;
        }
        case "ABS": {
            const val = toNum(args[0] ?? null);
            return isNaN(val) ? null : Math.abs(val);
        }
        case "MIN": {
            const nums = args.map(toNum).filter((n) => !isNaN(n));
            return nums.length === 0 ? null : Math.min(...nums);
        }
        case "MAX": {
            const nums = args.map(toNum).filter((n) => !isNaN(n));
            return nums.length === 0 ? null : Math.max(...nums);
        }
        case "IF": {
            const cond = args[0] ?? null;
            const thenVal = args[1] ?? null;
            const elseVal = args[2] ?? null;
            return toBool(cond) ? thenVal : elseVal;
        }
        // First non-null argument. Empty string is NOT null — "" is returned as-is.
        case "COALESCE": {
            for (const a of args) {
                if (a !== null && a !== undefined)
                    return a;
            }
            return null;
        }
        // S-5 §6 — pattern/flags are literal-only (enforced at the publish gate,
        // see regex-checkdigit-validate.ts). Runtime here just evaluates against
        // already-evaluated args; an unparseable pattern (shouldn't happen post
        // publish-gate) -> false, never throw.
        case "REGEX_MATCH": {
            const value = args[0] === null || args[0] === undefined ? "" : String(args[0]);
            const pattern = args[1] === null || args[1] === undefined ? "" : String(args[1]);
            const flags = args[2] === null || args[2] === undefined ? "" : String(args[2]);
            try {
                return new RegExp(pattern, flags).test(value);
            }
            catch {
                return false;
            }
        }
        // S-5 §7 — delegates to the pure, never-throwing checkDigitValid. The
        // publish gate (regex-checkdigit-validate.ts) enforces scheme is one of
        // the 3 literal values before content can ship, but the AST/evaluator
        // seam has no way to carry that narrowing through — the cast is safe
        // because checkDigitValid's `default: return false` handles any other
        // runtime string without throwing.
        case "CHECK_DIGIT": {
            const scheme = args[0] === null || args[0] === undefined ? "" : String(args[0]);
            const value = args[1] === null || args[1] === undefined ? "" : String(args[1]);
            return checkDigitValid(scheme, value);
        }
        // Quality — count of the document's fields whose OCR confidence is strictly
        // below the threshold. Absent confidence map ⇒ null (site-honest — the
        // caller cannot distinguish "0 low-confidence fields" from "not measured").
        case "CONFIDENCE_COUNT_BELOW": {
            const conf = context.fieldConfidence;
            if (!conf)
                return null;
            const threshold = toNum(args[0] ?? null);
            if (isNaN(threshold))
                return null;
            return Object.values(conf).filter((c) => c < threshold).length;
        }
        // String
        case "LEN": {
            const s = args[0] ?? null;
            return s === null ? 0 : String(s).length;
        }
        case "UPPER": {
            const v = args[0] ?? null;
            return v === null ? null : String(v).toUpperCase();
        }
        case "LOWER": {
            const v = args[0] ?? null;
            return v === null ? null : String(v).toLowerCase();
        }
        case "TRIM": {
            const v = args[0] ?? null;
            return v === null ? null : String(v).trim();
        }
        case "LEFT": {
            const v = args[0] ?? null;
            if (v === null)
                return null;
            const n = toNum(args[1] ?? 0);
            if (isNaN(n) || n <= 0)
                return "";
            return sliceChars(v, 0, Math.floor(n));
        }
        case "RIGHT": {
            const v = args[0] ?? null;
            if (v === null)
                return null;
            const s = String(v);
            const n = toNum(args[1] ?? 0);
            if (isNaN(n) || n <= 0)
                return "";
            const start = Math.max(0, s.length - Math.floor(n));
            return sliceChars(s, start, s.length);
        }
        case "MID":
        case "SUBSTRING": {
            const v = args[0] ?? null;
            if (v === null)
                return null;
            const start1 = toNum(args[1] ?? 1);
            const len = toNum(args[2] ?? 0);
            if (isNaN(start1) || isNaN(len) || len <= 0)
                return "";
            const start0 = Math.max(0, Math.floor(start1) - 1); // 1-based -> 0-based
            return sliceChars(v, start0, start0 + Math.floor(len));
        }
        case "FIND": {
            return findIndex1(args[0] ?? null, args[1] ?? null);
        }
        case "REPLACE": {
            return replaceAllLiteral(args[0] ?? null, args[1] ?? null, args[2] ?? null);
        }
        case "CONCAT":
            return args.map((a) => (a === null ? "" : String(a))).join("");
        // Date
        case "TODAY":
            return new Date().toISOString().slice(0, 10);
        // Temporal extractors (F-025) — operate on an ISO timestamp string.
        // UTC-based for determinism (batch scannedAt/createdAt are stored UTC).
        case "HOUR": {
            const d = args[0] === null || args[0] === undefined ? null : new Date(String(args[0]));
            return d && !isNaN(d.getTime()) ? d.getUTCHours() : null;
        }
        case "MINUTE": {
            const d = args[0] === null || args[0] === undefined ? null : new Date(String(args[0]));
            return d && !isNaN(d.getTime()) ? d.getUTCMinutes() : null;
        }
        case "DAY_OF_WEEK": {
            const d = args[0] === null || args[0] === undefined ? null : new Date(String(args[0]));
            return d && !isNaN(d.getTime()) ? d.getUTCDay() : null; // 0=Sun..6=Sat
        }
        case "DATE_DIFF": {
            const d1 = args[0] ?? null;
            const d2 = args[1] ?? null;
            const unit = args[2] ?? null;
            if (d1 === null || d2 === null)
                return null;
            const ms = new Date(String(d1)).getTime() - new Date(String(d2)).getTime();
            if (isNaN(ms))
                return null;
            const u = String(unit ?? "days").toLowerCase();
            if (u === "days")
                return Math.floor(ms / 86_400_000);
            if (u === "hours")
                return Math.floor(ms / 3_600_000);
            return Math.floor(ms / 86_400_000);
        }
        case "DATE_ADD": {
            const dateVal = args[0] ?? null;
            const amount = args[1] ?? null;
            const dateUnit = args[2] ?? null;
            if (dateVal === null || amount === null)
                return null;
            const d = new Date(String(dateVal));
            if (isNaN(d.getTime()))
                return null;
            const n = toNum(amount);
            const du = String(dateUnit ?? "days").toLowerCase();
            if (du === "days")
                d.setDate(d.getDate() + n);
            else if (du === "months")
                d.setMonth(d.getMonth() + n);
            else if (du === "years")
                d.setFullYear(d.getFullYear() + n);
            else
                d.setDate(d.getDate() + n);
            return d.toISOString().slice(0, 10);
        }
        // --- P4: date constructors / parts (UTC, no JS rollover) ---
        case "DATE": {
            const y = intArg(args[0]);
            const m = intArg(args[1]);
            const d = intArg(args[2]);
            if (y === null || m === null || d === null)
                return null;
            return buildUtcDateIso(y, m, d);
        }
        case "YEAR": {
            const dt = parseUtcDate(args[0] ?? null);
            return dt ? dt.getUTCFullYear() : null;
        }
        case "MONTH": {
            const dt = parseUtcDate(args[0] ?? null);
            return dt ? dt.getUTCMonth() + 1 : null; // 1-12
        }
        case "DAY": {
            const dt = parseUtcDate(args[0] ?? null);
            return dt ? dt.getUTCDate() : null;
        }
        case "QUARTER": {
            const dt = parseUtcDate(args[0] ?? null);
            return dt ? Math.floor(dt.getUTCMonth() / 3) + 1 : null; // 1-4
        }
        case "QUARTER_START": {
            const y = intArg(args[0]);
            const q = intArg(args[1]);
            if (y === null || q === null || q < 1 || q > 4)
                return null;
            return buildUtcDateIso(y, (q - 1) * 3 + 1, 1); // months 1/4/7/10
        }
        case "QUARTER_END": {
            const y = intArg(args[0]);
            const q = intArg(args[1]);
            if (y === null || q === null || q < 1 || q > 4)
                return null;
            return lastDayOfMonthIso(y, q * 3 - 1); // last day of months Mar/Jun/Sep/Dec (0-based)
        }
        case "EOMONTH": {
            const dt = parseUtcDate(args[0] ?? null);
            if (!dt)
                return null;
            const offset = args[1] === undefined || args[1] === null ? 0 : intArg(args[1]);
            if (offset === null)
                return null;
            return lastDayOfMonthIso(dt.getUTCFullYear(), dt.getUTCMonth() + offset);
        }
        // --- P4/S-4: business days (weekends + tenant-configured holiday calendar) ---
        // Review must-fix: both loops run synchronously on the event loop and are
        // NOT covered by MAX_STEPS (they never re-enter eval_), so a garbage span
        // (e.g. a mis-OCR'd year landing in 0001) must return null instead of
        // iterating millions of days. ~100 years is far beyond any legitimate
        // business-day window; beyond it the input is bad data, and bad data gets
        // the same null every other invalid date input gets.
        case "NETWORKDAYS": {
            const a = parseUtcDate(args[0] ?? null);
            const b = parseUtcDate(args[1] ?? null);
            if (!a || !b)
                return null;
            let start = utcMidnight(a);
            let end = utcMidnight(b);
            let sign = 1;
            if (start > end) {
                [start, end] = [end, start];
                sign = -1;
            }
            if (end.getTime() - start.getTime() > MAX_BUSINESS_DAY_SPAN_DAYS * 86_400_000)
                return null;
            let count = 0;
            for (let t = start.getTime(); t <= end.getTime(); t += 86_400_000) {
                const cur = new Date(t);
                if (!isWeekend(cur.getUTCDay()) && !isHoliday(cur, context))
                    count++;
            }
            return sign * count;
        }
        case "WORKDAY": {
            const dt = parseUtcDate(args[0] ?? null);
            const n = intArg(args[1]);
            if (!dt || n === null)
                return null;
            if (Math.abs(n) > MAX_BUSINESS_DAY_SPAN_DAYS)
                return null;
            let cur = utcMidnight(dt);
            let remaining = Math.abs(n);
            const dir = n >= 0 ? 1 : -1;
            while (remaining > 0) {
                cur = new Date(cur.getTime() + dir * 86_400_000);
                if (!isWeekend(cur.getUTCDay()) && !isHoliday(cur, context))
                    remaining--;
            }
            return isoDate(cur);
        }
        default:
            return null;
    }
}
/** Review must-fix — hard ceiling on business-day iteration (~100 years).
 *  Spans/offsets beyond this are garbage data (mis-OCR'd years), not real
 *  business windows; they return null rather than blocking the event loop.
 *  Unit nuance (reviewer-noted): NETWORKDAYS caps the CALENDAR-day span
 *  (loop iterations ≤ the constant); WORKDAY caps the requested BUSINESS-day
 *  count |n| (its calendar-day loop runs ≈ n·7/5 ≈ 51k iterations worst case —
 *  still trivially fast; both bounds exist to kill the multi-second class). */
const MAX_BUSINESS_DAY_SPAN_DAYS = 36_500;
// --- P4 date helpers (all UTC; invalid input → null) ---
/** Coerce an arg to an integer, or null if non-numeric / non-integer.
 *  (Number.isInteger is already false for NaN/±Infinity, so it's the whole test.) */
function intArg(val) {
    if (val === null || val === undefined)
        return null;
    const n = toNum(val);
    return Number.isInteger(n) ? n : null;
}
/** Parse an ISO date/datetime string to a Date, or null if unparseable. */
function parseUtcDate(val) {
    if (val === null || val === undefined)
        return null;
    const d = new Date(String(val));
    return isNaN(d.getTime()) ? null : d;
}
/** Format a Date as YYYY-MM-DD (UTC). */
function isoDate(d) {
    return d.toISOString().slice(0, 10);
}
/** Strip time, returning a UTC-midnight Date for the same calendar day. */
function utcMidnight(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}
function isWeekend(utcDay) {
    return utcDay === 0 || utcDay === 6; // 0=Sun, 6=Sat
}
/** S-4 — true when `d` (UTC midnight) is a tenant-configured holiday, either
 *  an exact-date entry or a recurring MM-DD entry matched against any year.
 *  Absent `context.holidays` → false (weekends-only, unchanged behavior).
 *  The `&&` in both call sites already prevents double-subtracting a holiday
 *  that also falls on a weekend — this function does not need to know about
 *  weekends at all. */
function isHoliday(d, context) {
    const holidays = context.holidays;
    if (!holidays)
        return false;
    const iso = isoDate(d);
    if (holidays.dates.includes(iso))
        return true;
    return holidays.recurringMonthDay.includes(iso.slice(5));
}
/** Build a YYYY-MM-DD from 1-based (y, m, d), rejecting any component that JS
 *  Date would silently roll over (e.g. month 13, Feb 30) by round-trip check. */
function buildUtcDateIso(y, m1, d) {
    if (m1 < 1 || m1 > 12 || d < 1 || d > 31)
        return null;
    const dt = new Date(Date.UTC(y, m1 - 1, d));
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m1 - 1 || dt.getUTCDate() !== d) {
        return null; // JS rolled the date over — reject
    }
    return isoDate(dt);
}
/** Last calendar day of the given (year, 0-based month), as YYYY-MM-DD. The
 *  month index may be out of 0-11 range (offset arithmetic) — Date.UTC carries
 *  it into the correct year, which is intended for EOMONTH offsets. */
function lastDayOfMonthIso(y, m0) {
    return isoDate(new Date(Date.UTC(y, m0 + 1, 0)));
}
// --- Line item aggregation ---
function evalLineItemAgg(name, argNodes, context, eval_) {
    if (argNodes.length === 0)
        return name === "COUNT_LINES" ? 0 : null;
    const arg = argNodes[0];
    if (arg.type !== "fieldRef")
        return null;
    // Parse "group.field" or just "group" from the fieldRef.
    const dot = arg.name.indexOf(".");
    const groupName = dot < 0 ? arg.name : arg.name.slice(0, dot);
    const fieldName = dot < 0 ? null : arg.name.slice(dot + 1);
    const rows = context.lineItems?.[groupName] ?? [];
    // Optional 2nd arg = per-row filter AST, evaluated via the shared overlay
    // (same machinery TXN_* uses). Skipped entirely when there's no filter.
    const filterNode = argNodes[1];
    const overlay = makeRowOverlay(context, groupName, filterNode);
    // COUNT_LINES(group[, filter]) — bare group, counts (filtered) rows.
    if (name === "COUNT_LINES") {
        let count = 0;
        for (const row of rows) {
            if (passesFilter(filterNode, overlay, row, eval_))
                count++;
        }
        return count;
    }
    // COUNT_DISTINCT_LINES(group.col[, filter]) — distinct non-null values,
    // String()-normalized so 1 and "1" collapse. Needs group.col.
    if (name === "COUNT_DISTINCT_LINES") {
        if (!fieldName)
            return null;
        const seen = new Set();
        for (const row of rows) {
            if (!passesFilter(filterNode, overlay, row, eval_))
                continue;
            const v = row[fieldName];
            if (v === undefined || v === null)
                continue;
            seen.add(String(v));
        }
        return seen.size;
    }
    // SUM/AVG/MIN/MAX need group.col.
    if (!fieldName)
        return null;
    // Single pass over (filtered) rows. Line items keep the LEGACY LENIENT stance:
    // a non-numeric cell is NaN-FILTERED (dropped), NOT null-poisoned like TXN_SUM.
    // (TXN's null-poison is a remittance-correctness stance; line items stay
    // lenient for back-compat with existing compute expressions.)
    let matched = 0;
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    for (const row of rows) {
        if (!passesFilter(filterNode, overlay, row, eval_))
            continue;
        const raw = row[fieldName];
        const n = typeof raw === "number" ? raw : Number(raw);
        if (isNaN(n))
            continue;
        matched++;
        sum += n;
        if (n < min)
            min = n;
        if (n > max)
            max = n;
    }
    switch (name) {
        case "SUM_LINES":
            return sum; // empty / no-match stays 0
        case "AVG_LINES":
            return matched === 0 ? null : sum / matched; // EMPTY → null (was 0)
        case "MIN_LINES":
            return matched === 0 ? null : min; // EMPTY → null (was 0)
        case "MAX_LINES":
            return matched === 0 ? null : max; // EMPTY → null (was 0)
        default:
            return null;
    }
}
// --- Batch aggregation ---
function evalBatchAgg(name, argNodes, context) {
    const docs = context.batch?.documents;
    if (!docs)
        return null;
    if (name === "BATCH_COUNT")
        return docs.length;
    // All other BATCH_* need a field ref argument
    if (argNodes.length === 0)
        return null;
    const arg = argNodes[0];
    if (arg.type !== "fieldRef")
        return null;
    const fieldName = arg.name;
    const values = docs
        .map((doc) => doc[fieldName])
        .filter((v) => v != null);
    // BATCH_ALL_EQUAL: string-normalized equality across the non-null values.
    // 0 or 1 value ⇒ trivially all-equal ⇒ true (documents present, so never null here).
    if (name === "BATCH_ALL_EQUAL") {
        if (values.length <= 1)
            return true;
        const first = String(values[0]);
        return values.every((v) => String(v) === first);
    }
    if (values.length === 0)
        return name === "BATCH_SUM" ? 0 : null;
    switch (name) {
        case "BATCH_SUM":
            return values.reduce((sum, v) => sum + (typeof v === "number" ? v : Number(v) || 0), 0);
        case "BATCH_AVG": {
            const nums = values
                .map((v) => (typeof v === "number" ? v : Number(v)))
                .filter((n) => !isNaN(n));
            return nums.length === 0
                ? null
                : nums.reduce((s, v) => s + v, 0) / nums.length;
        }
        case "BATCH_MIN": {
            const nums = values
                .map((v) => (typeof v === "number" ? v : Number(v)))
                .filter((n) => !isNaN(n));
            return nums.length === 0 ? null : Math.min(...nums);
        }
        case "BATCH_MAX": {
            const nums = values
                .map((v) => (typeof v === "number" ? v : Number(v)))
                .filter((n) => !isNaN(n));
            return nums.length === 0 ? null : Math.max(...nums);
        }
        case "BATCH_FIRST":
            return values[0] ?? null;
        case "BATCH_LAST":
            return values[values.length - 1] ?? null;
        default:
            return null;
    }
}
// --- Transaction (TXN_*) aggregation (SP5) ---
function evalTxnAgg(name, argNodes, context, eval_) {
    if (!context.txnRows)
        return null;
    const arg = argNodes[0];
    if (!arg || arg.type !== "fieldRef")
        return null;
    const filterNode = argNodes[1];
    // TXN_COUNT(coll, filter?) — bare collection name
    if (name === "TXN_COUNT") {
        if (!TXN_COLLECTIONS.has(arg.name))
            return null;
        const coll = arg.name;
        const overlay = makeRowOverlay(context, coll, filterNode);
        let count = 0;
        for (const row of context.txnRows[coll]) {
            if (passesFilter(filterNode, overlay, row, eval_))
                count++;
        }
        return count;
    }
    // TXN_SUM/MIN/MAX(coll.key, filter?) — dotted ref
    const dot = arg.name.indexOf(".");
    if (dot < 0)
        return null;
    const collName = arg.name.slice(0, dot);
    const key = arg.name.slice(dot + 1);
    if (!TXN_COLLECTIONS.has(collName))
        return null;
    const coll = collName;
    const overlay = makeRowOverlay(context, coll, filterNode);
    // Single pass: filter, null-poison check, and sum/min/max accumulation
    // together — avoids materializing an intermediate matched-rows array and
    // the stack-overflow-prone Math.min/max(...array) spread on large inputs.
    let matched = 0;
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    for (const row of context.txnRows[coll]) {
        if (!passesFilter(filterNode, overlay, row, eval_))
            continue;
        const raw = row[key];
        const n = typeof raw === "number" ? raw : parseAmountToCents(raw);
        if (n === null || Number.isNaN(n))
            return null; // null-poisoning: any matched row's unresolved value nullifies the aggregate
        matched++;
        sum += n;
        if (n < min)
            min = n;
        if (n > max)
            max = n;
    }
    if (matched === 0)
        return name === "TXN_SUM" ? 0 : null;
    switch (name) {
        case "TXN_SUM":
            return sum;
        case "TXN_MIN":
            return min;
        case "TXN_MAX":
            return max;
        default:
            return null;
    }
}
/** Build the per-row overlay once per aggregate call (not once per row) —
 *  it is a shallow clone of the outer context plus a mutable `_rowOverlay` slot
 *  whose `row` is swapped in before each filter evaluation. Skipped entirely
 *  when there's no filter to evaluate. `head` is the collection/group name whose
 *  dotted refs resolve from the row. Shared by TXN_* and *_LINES aggregates. */
function makeRowOverlay(context, head, filterNode) {
    if (!filterNode)
        return undefined;
    return { ...context, _rowOverlay: { head, row: {} } };
}
/** Evaluate the optional filter AST once per row: dotted refs of THIS
 *  collection/group resolve from the row (head stripped); everything else
 *  falls through to the outer context. Truthy ⇒ keep. Null/falsy ⇒ drop
 *  (null-filter = non-match). Each row's filter evaluation runs through the
 *  shared `eval_` step counter, so a pathological per-row filter still trips
 *  MAX_STEPS. */
function passesFilter(filterNode, overlay, row, eval_) {
    if (!filterNode || !overlay)
        return true;
    overlay._rowOverlay.row = row;
    const v = eval_(filterNode, overlay);
    return v === true || (typeof v === "number" && v !== 0);
}
// --- Helpers ---
function toNum(val) {
    if (val === null)
        return NaN;
    if (typeof val === "number")
        return val;
    if (typeof val === "boolean")
        return val ? 1 : 0;
    const n = Number(val);
    return n;
}
function toBool(val) {
    if (val === null)
        return false;
    if (typeof val === "boolean")
        return val;
    if (typeof val === "number")
        return val !== 0;
    if (typeof val === "string")
        return val !== "" && val !== "0" && val !== "false";
    return Boolean(val);
}
function looseEquals(a, b) {
    if (a === b)
        return true;
    if (a === null || b === null)
        return false;
    // Number coercion
    const na = toNum(a);
    const nb = toNum(b);
    if (!isNaN(na) && !isNaN(nb))
        return na === nb;
    return String(a) === String(b);
}
//# sourceMappingURL=evaluator.js.map